import type { WorkflowEventResponse } from '@/lib/api';

export type WorkflowActionType = 'cancel' | 'abandon' | 'approve' | 'reject' | 'delete';

export interface WorkflowActionEventView {
  id: string;
  action: WorkflowActionType;
  title: string;
  detail: string | null;
  timestamp: number;
  statusBefore: string | null;
}

function isWorkflowActionType(value: unknown): value is WorkflowActionType {
  return (
    value === 'cancel' ||
    value === 'abandon' ||
    value === 'approve' ||
    value === 'reject' ||
    value === 'delete'
  );
}

export function formatWorkflowActionTitle(action: WorkflowActionType): string {
  switch (action) {
    case 'cancel':
      return 'Cancelled';
    case 'abandon':
      return 'Abandoned';
    case 'approve':
      return 'Approved';
    case 'reject':
      return 'Rejected';
    case 'delete':
      return 'Deleted';
  }
}

export function formatWorkflowActionDetail(data: Record<string, unknown>): string | null {
  const parts: string[] = [];
  const workflowName = typeof data.workflow_name === 'string' ? data.workflow_name : null;
  const statusBefore = typeof data.status_before === 'string' ? data.status_before : null;
  const approvalNodeId = typeof data.approval_node_id === 'string' ? data.approval_node_id : null;
  const reason = typeof data.reason === 'string' ? data.reason : null;
  const comment = typeof data.comment === 'string' ? data.comment : null;

  if (workflowName) parts.push(workflowName);
  if (statusBefore) parts.push(`from ${statusBefore}`);
  if (approvalNodeId) parts.push(`at ${approvalNodeId}`);
  if (reason) parts.push(`reason: ${reason}`);
  if (comment) parts.push(`comment: ${comment}`);

  return parts.length > 0 ? parts.join(' · ') : null;
}

export function getWorkflowActionEvents(
  events: WorkflowEventResponse[]
): WorkflowActionEventView[] {
  return events
    .filter((event): event is WorkflowEventResponse & { event_type: 'workflow_action' } => {
      if (event.event_type !== 'workflow_action') return false;
      const action = event.data.action;
      return isWorkflowActionType(action);
    })
    .map(event => {
      const action = event.data.action as WorkflowActionType;
      return {
        id: event.id,
        action,
        title: formatWorkflowActionTitle(action),
        detail: formatWorkflowActionDetail(event.data),
        timestamp: new Date(event.created_at).getTime(),
        statusBefore:
          typeof event.data.status_before === 'string' ? event.data.status_before : null,
      };
    })
    .sort((a, b) => a.timestamp - b.timestamp);
}
